import { Column, ColumnDef, Row } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown, Trash } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { Gate } from "../../../hooks/common/interface";
import { useTranslation } from "react-i18next";
import DeleteGateModal from "../../modal/DeleteGateModal";

interface GatesConfigTableProps {
  gates: Gate[];
  setSelectedGateId: Dispatch<SetStateAction<number>>;
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const GatesConfigTable = ({
  gates,
  setSelectedGateId,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  pageSize,
  setPageSize,
}: GatesConfigTableProps) => {
  const { i18n, t } = useTranslation();

  const [isDeleteGateModalOpen, setIsDeleteGateModalOpen] = useState(false);
  const [selectedDeleteGateId, setSelectedDeleteGateId] = useState(0);

  const handleDeleteGate = useCallback(async (gateId: number) => {
    setIsDeleteGateModalOpen(true);
    setSelectedDeleteGateId(gateId);
  }, []);

  const columns: ColumnDef<Gate>[] = useMemo(
    () => [
      {
        accessorKey: "n_GateID",
        header: ({ column }: { column: Column<Gate> }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("gateId")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }: { row: Row<Gate> }) => {
          return (
            <span
              onClick={() => setSelectedGateId(row.original.n_GateID)}
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.n_GateID}
            </span>
          );
        },
      },
      {
        accessorKey: "s_GateName_En",
        header: ({ column }: { column: Column<Gate> }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("gateNameEnglish")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }: { row: Row<Gate> }) => {
          return <span>{row.original.s_GateName_En}</span>;
        },
      },
      {
        accessorKey: "s_GateName_Ar",
        header: ({ column }: { column: Column<Gate> }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("gateNameArabic")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }: { row: Row<Gate> }) => {
          return <span>{row.original.s_GateName_Ar}</span>;
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }: { column: Column<Gate> }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }: { row: Row<Gate> }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_BuildingName_En",
        header: ({ column }: { column: Column<Gate> }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("building")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }: { row: Row<Gate> }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_BuildingName_En
                : row.original.s_BuildingName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "action",
        header: t("action"),
        cell: ({ row }: { row: Row<Gate> }) => {
          return (
            <Trash
              onClick={() => handleDeleteGate(row.original.n_GateID)}
              className="w-5 h-5 text-red-600 cursor-pointer"
            />
          );
        },
      },
    ],
    // .filter((column) => {
    //   let excludeKeys = [];

    //   const isDeleteGatePrivilege = userRoles?.rolePrivileges?.find(
    //     (item) => item.s_RouteName === ROUTE_NAMES.GATES_DELETE_CONFIG
    //   );

    //   if (!isDeleteGatePrivilege) {
    //     excludeKeys.push("action");
    //   }

    //   return !excludeKeys.includes(column.accessorKey);
    // })
    [handleDeleteGate, i18n.language, setSelectedGateId, t]
  );

  return (
    <>
      <TableWrapper
        data={gates}
        columns={columns}
        isColumnChooser={false}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <DeleteGateModal
        gateId={selectedDeleteGateId}
        isOpen={isDeleteGateModalOpen}
        onOpenChange={() => setIsDeleteGateModalOpen(false)}
      />
    </>
  );
};

export default React.memo(GatesConfigTable);
